import { ActionContext, ActionTree } from "vuex";
import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";
import { ISalesState } from "@/core/interfaces/salesState.interface";
import { StatusCodes } from "http-status-codes";
import OrganizationService from "@/modules/Organization/services/organization.service";
import SalesService from "../services/sales.service";
import analyticServices from "@/analytic.service";
import { SALES_ANALYTICS_EVENTS } from "../constants/analyticsEvents";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { SalesProductivityParamsRequestModel } from "../models/FetchSalesProductivityParams.request";
import { StoreState } from "@/store/type";

const actions: ActionTree<ISalesState, StoreState> = {
  // *********** Get Sales Sectors ***********
  async fetchSalesSectors(context: ActionContext<ISalesState, StoreState>) {
    try {
      context.commit("setFetchSectorsLoading", true);
      context.commit("setSalesSectors", null);
      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getFormDetails();
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setSalesSectors", response.data?.sector);
        return response.data;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      // Handle error silently
    } finally {
      context.commit("setFetchSectorsLoading", false);
    }
  },

  // *********** Get Sales Stats ***********
  async getSalesStats(
    context: ActionContext<ISalesState, StoreState>,
    salesmanId,
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setSalesLoading", true);
      context.commit("setSalesSnapshot", null);
      context.commit("setSalesProductivity", null);
      const salesService = new SalesService();
      response = await salesService.getSalesSnapshot(salesmanId);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setSalesSnapshot", response.data?.data);
        analyticServices.logActionEvent(
          SALES_ANALYTICS_EVENTS.SALES_STATS_FETCHED,
          {
            salesmanId,
          },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          "Sales statistics fetched successfully";
      } else {
        throw response;
      }
    } catch (error: unknown) {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setSalesLoading", false);
    }
    notificationService.triggerNotification();
  },

  // *********** Get Sales Productivity ***********
  async getSalesProductivity(
    context: ActionContext<ISalesState, StoreState>,
    {
      payload,
      salesmanId,
    }: {
      payload: SalesProductivityParamsRequestModel;
      salesmanId: string;
    },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setSalesLoading", true);
      context.commit("setSalesProductivity", null);
      const salesService = new SalesService();
      response = await salesService.getSalesProductivity(salesmanId, payload);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setSalesProductivity", response.data?.data);
        analyticServices.logActionEvent(
          SALES_ANALYTICS_EVENTS.SALES_PRODUCTIVIY_FETCHED,
          {
            salesmanId,
            ...payload,
          },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          "Sales Productivity fetched successfully";
      } else {
        throw response;
      }
    } catch (error: unknown) {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setSalesLoading", false);
    }
    notificationService.triggerNotification();
  },

  // *********** Get Sales My Team ***********
  async getSalesMyTeam(context: ActionContext<ISalesState, StoreState>) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setSalesMyTeamLoading", true);
      context.commit("setSalesMyTeam", []);
      const salesService = new SalesService();
      response = await salesService.getSalesMyTeam();
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setSalesMyTeam", response.data?.data?.team);
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = "Team Members Successfully Fetched";
        analyticServices.logActionEvent(
          SALES_ANALYTICS_EVENTS.SALES_MY_TEAM_FETCHED,
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setSalesMyTeamLoading", false);
    }
    notificationService.triggerNotification();
  },
};

export default actions;
